import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ApiError,
  type CurrentUser,
  api,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  setUnauthorizedHandler,
} from "./api";

type AuthStatus = "checking" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: AuthStatus;
  user: CurrentUser | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("checking");

  const handleSignedOut = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    setStatus("anonymous");
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(handleSignedOut);
    return () => setUnauthorizedHandler(null);
  }, [handleSignedOut]);

  useEffect(() => {
    // On boot: if a refresh token exists, fetch /me/ — the api client will
    // refresh-then-retry transparently.
    let cancelled = false;
    const refresh = getRefreshToken();
    if (!refresh) {
      setStatus("anonymous");
      return;
    }
    api
      .me()
      .then((current) => {
        if (cancelled) return;
        setUser(current);
        setStatus("authenticated");
      })
      .catch(() => {
        if (cancelled) return;
        handleSignedOut();
      });
    return () => {
      cancelled = true;
    };
  }, [handleSignedOut]);

  const signIn = useCallback(async (email: string, password: string) => {
    const data = await api.login(email, password);
    setAccessToken(data.access);
    setRefreshToken(data.refresh);
    setUser(data.user);
    setStatus("authenticated");
  }, []);

  const signOut = useCallback(async () => {
    const refresh = getRefreshToken();
    if (refresh && getAccessToken()) {
      try {
        await api.logout(refresh);
      } catch (err) {
        if (!(err instanceof ApiError)) {
          throw err;
        }
      }
    }
    handleSignedOut();
  }, [handleSignedOut]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, signIn, signOut }),
    [status, user, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
